import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Group, useMantineTheme } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import * as C from '../../../../ui/common/CommonStyles';
import { queries } from '../../../../../shared/queries';
import type { IModemRegisterFormValues } from '../../../../../shared/types/modem';
import type { IModemUpsertPayload } from '../../../../../shared/apis/modem/types';
import { getLocaleDate } from '../../../../../shared/utils/time';
import { modemRegisterSchema } from './utils/modemRegisterSchema';
import ModemInfoForm from './ui/modemInfoForm';
import usePostModem from './hooks/usePostModem';
import useGetModemDetails from './hooks/useGetModemDetails';
import { showSuccessNotification } from '../../../../ui/notification';
import usePutModem from './hooks/usePutModem';

function normalizeOptionalString(value: string | null | undefined): string {
  return value?.trim() || '';
}

function buildServiceNumber(data: IModemRegisterFormValues): string {
  const serviceNumberParts = [data.serviceNumber1, data.serviceNumber2, data.serviceNumber3].map((value) =>
    normalizeOptionalString(value),
  );

  if (serviceNumberParts.every((value) => !value)) {
    return '';
  }

  return serviceNumberParts.join('-');
}

export default function ModemRegisterContainer() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = !!id;
  const {
    register,
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<IModemRegisterFormValues>({
    resolver: yupResolver(modemRegisterSchema),
    defaultValues: {
      modem: '',
      modemNo: '',
      modemImei: '',
      statusYn: '',
      telecom: '',
      plan: '',
      usimNo: '',
      serviceNumber1: '',
      serviceNumber2: '',
      serviceNumber3: '',
      serviceNo: '',
      activateStatus: '',
      contractPeriod: '',
      activateStartDt: null,
      activateEndDt: null,
      memo: '',
    },
  });

  const onCancel = () => {
    Swal.fire({
      title: '등록 취소',
      text: '작성 중인 내용은 저장되지 않습니다.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: theme.colors.red[5],
      confirmButtonText: '취소',
      cancelButtonText: '계속 작성',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/admin/management/modem');
      }
    });
  };

  const { mutate: postModem } = usePostModem();
  const { mutate: putModem } = usePutModem();

  const onSubmit = (data: IModemRegisterFormValues) => {
    const serviceNo = buildServiceNumber(data);
    const requestPayload: IModemUpsertPayload = {
      modem: data.modem,
      modemNo: data.modemNo,
      modemImei: normalizeOptionalString(data.modemImei),
      statusYn: normalizeOptionalString(data.statusYn),
      telecom: normalizeOptionalString(data.telecom),
      plan: normalizeOptionalString(data.plan),
      usimNo: normalizeOptionalString(data.usimNo),
      serviceNo,
      activateStatus: data.activateStatus ? normalizeOptionalString(data.activateStatus) : null,
      contractPeriod: data.contractPeriod ? Number(data.contractPeriod) : null,
      activateStartDt: data.activateStartDt ? data.activateStartDt.toISOString().substring(0, 19) : null,
      activateEndDt: data.activateEndDt ? data.activateEndDt.toISOString().substring(0, 19) : null,
      memo: normalizeOptionalString(data.memo),
    };

    if (id) {
      putModem(
        { id: Number(id), data: requestPayload },
        {
          onSuccess: (data) => {
            showSuccessNotification('수정 되었습니다.');
            if (id) {
              queryClient.invalidateQueries({
                queryKey: queries.modems.modemDetails({ id: Number(id) }).queryKey,
              });
            }
            queryClient.invalidateQueries(queries.modems.modemList._def);
            navigate(`/admin/management/modem/details/${data.data.data.id}`);
          },
        },
      );
      return;
    }
    postModem(requestPayload, {
      onSuccess: (data) => {
        showSuccessNotification('등록 되었습니다.');
        queryClient.invalidateQueries(queries.modems.modemList._def);
        navigate(`/admin/management/modem/details/${data.data.data.id}`);
      },
    });
  };

  const { data: modemDetails } = useGetModemDetails({
    id: Number(id),
  });

  useEffect(() => {
    if (!modemDetails) {
      return;
    }

    const serviceNumberParts = normalizeOptionalString(modemDetails.serviceNo).split('-');

    reset({
      modem: normalizeOptionalString(modemDetails.modem),
      modemNo: normalizeOptionalString(modemDetails.modemNo),
      modemImei: normalizeOptionalString(modemDetails.modemImei),
      statusYn: normalizeOptionalString(modemDetails.statusYn),
      telecom: normalizeOptionalString(modemDetails.telecom),
      plan: normalizeOptionalString(modemDetails.plan),
      usimNo: normalizeOptionalString(modemDetails.usimNo),
      serviceNumber1: serviceNumberParts[0] || '',
      serviceNumber2: serviceNumberParts[1] || '',
      serviceNumber3: serviceNumberParts[2] || '',
      serviceNo: normalizeOptionalString(modemDetails.serviceNo),
      activateStatus: normalizeOptionalString(modemDetails.activateStatus),
      contractPeriod: modemDetails.contractPeriod ? String(modemDetails.contractPeriod) : '',
      activateStartDt: modemDetails.activateStartDt ? getLocaleDate(modemDetails.activateStartDt) : null,
      activateEndDt: modemDetails.activateEndDt ? getLocaleDate(modemDetails.activateEndDt) : null,
      memo: normalizeOptionalString(modemDetails.memo),
    });
  }, [modemDetails, reset]);

  return (
    <div>
      <C.TitleBox>
        <div className="title">{isEdit ? `모뎀 수정` : `모뎀 등록`}</div>
      </C.TitleBox>
      <C.ListBox>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModemInfoForm
            {...{
              register,
              errors,
              control,
              setValue,
              isEdit,
            }}
            initialModemImei={normalizeOptionalString(modemDetails?.modemImei)}
          />
          <Group justify="center" mt="lg">
            <Button type="submit" variant="filled" color="primary.5" leftSection={<IconPlus size={16} />}>
              저장
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} leftSection={<IconX size={16} />}>
              취소
            </Button>
          </Group>
        </form>
      </C.ListBox>
    </div>
  );
}
